import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Wrench, Zap, Pencil, Trash2, Loader2, Plus, X, CalendarDays, BatteryCharging } from "lucide-react";
import { fetchMaintenance, createMaintenance, updateMaintenance, deleteMaintenance, fetchVehicles, completeCharging } from "@/lib/api";
import { formatDMY } from "@/lib/date";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TimeField } from "@/components/TimeField";

const TYPES = ["Maintenance", "Charging"];

const emptyForm = { vehicle: "", block_type: "Maintenance", start_date: "", start_time: "09:00", end_date: "", end_time: "10:00" };

const toDateObj = (s) => {
  if (!s) return undefined;
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const calendarClassNames = {
  day_today: "ring-2 ring-[#5C2D91] ring-offset-1 rounded-md font-bold text-[#5C2D91]",
  day_selected: "bg-[#5C2D91] text-white hover:bg-[#4A2475] hover:text-white focus:bg-[#5C2D91] focus:text-white rounded-md",
  day: "h-9 w-9 p-0 font-normal rounded-md text-[#0F172A] transition-colors hover:bg-[#5C2D91]/10 aria-selected:opacity-100",
  caption_label: "text-sm font-semibold text-[#0F172A]",
  head_cell: "text-[#64748B] rounded-md w-9 font-medium text-[0.75rem]",
};

const fmtDT = (iso) => {
  if (!iso) return "—";
  return `${formatDMY(iso.slice(0, 10))} ${iso.slice(11, 16)}`;
};

const TypeIcon = ({ type, className }) =>
  type === "Charging" ? <Zap className={className} /> : <Wrench className={className} />;

const StatusBadge = ({ r }) => {
  if (r.pending_battery) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 ring-1 ring-amber-300" data-testid={`maint-status-${r.id}`}>
        <BatteryCharging className="h-3.5 w-3.5" /> Battery Update Required
      </span>
    );
  }
  if (r.block_type === "Charging" && r.completed) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-300" data-testid={`maint-status-${r.id}`}>
        Completed{r.battery_percentage != null && <span className="font-bold">· {r.battery_percentage}%</span>}
      </span>
    );
  }
  const label = r.block_type === "Charging" ? "Charging" : "Maintenance";
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-300" data-testid={`maint-status-${r.id}`}>
      {label}
    </span>
  );
};

const DateTimePicker = ({ label, dateVal, timeVal, onDate, onTime, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-[#475569]">{label}</label>
      <div className="flex gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid={`${testid}-date`}
              className="flex flex-1 items-center justify-between gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-[#0F172A] outline-none transition-all hover:border-[#5C2D91] focus:border-[#5C2D91] focus:ring-1 focus:ring-[#5C2D91]"
            >
              <span data-testid={`${testid}-date-value`}>{dateVal ? formatDMY(dateVal) : "DD/MM/YYYY"}</span>
              <CalendarDays className="h-4 w-4 shrink-0 text-[#5C2D91]" aria-hidden="true" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto rounded-xl border-slate-200 p-0 shadow-lg">
            <Calendar
              mode="single"
              selected={toDateObj(dateVal)}
              onSelect={(d) => { if (d) { onDate(toDateStr(d)); setOpen(false); } }}
              defaultMonth={toDateObj(dateVal)}
              initialFocus
              classNames={calendarClassNames}
            />
          </PopoverContent>
        </Popover>
        <TimeField value={timeVal} onChange={onTime} className="w-36 shrink-0" testid={`${testid}-time`} />
      </div>
    </div>
  );
};

export const MaintenanceTab = ({ onChanged }) => {
  const [records, setRecords] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [deleteRec, setDeleteRec] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [batteryRec, setBatteryRec] = useState(null);
  const [batteryVal, setBatteryVal] = useState("");
  const [savingBattery, setSavingBattery] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [recs, vs] = await Promise.all([fetchMaintenance(), fetchVehicles()]);
      setRecords(recs);
      setVehicles(vs);
      setForm((f) => (f.vehicle ? f : { ...f, vehicle: vs[0]?.name || "" }));
    } catch {
      toast.error("Failed to load maintenance records");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const resetForm = () => {
    setForm(emptyForm);
    setEditId(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.start_date || !form.start_time || !form.end_date || !form.end_time) {
      toast.error("Select start and end date & time");
      return;
    }
    const start_at = `${form.start_date}T${form.start_time}`;
    const end_at = `${form.end_date}T${form.end_time}`;
    if (end_at <= start_at) {
      toast.error("End time must be after start time");
      return;
    }
    const payload = { vehicle: form.vehicle, block_type: form.block_type, start_at, end_at };
    setSaving(true);
    try {
      if (editId) {
        await updateMaintenance(editId, payload);
        toast.success("Record updated");
      } else {
        await createMaintenance(payload);
        toast.success("Record saved");
      }
      resetForm();
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save record");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (r) => {
    setEditId(r.id);
    setForm({
      vehicle: r.vehicle,
      block_type: r.block_type,
      start_date: r.start_at.slice(0, 10),
      start_time: r.start_at.slice(11, 16),
      end_date: r.end_at.slice(0, 10),
      end_time: r.end_at.slice(11, 16),
    });
  };

  const confirmDelete = async () => {    setDeleting(true);
    try {
      await deleteMaintenance(deleteRec.id);
      toast.success("Record deleted");
      if (editId === deleteRec.id) resetForm();
      setDeleteRec(null);
      await load();
      onChanged?.();
    } catch {
      toast.error("Could not delete record");
    } finally {
      setDeleting(false);
    }
  };

  const submitBattery = async () => {
    const val = Number(batteryVal);
    if (!/^\d+$/.test(String(batteryVal).trim()) || !Number.isInteger(val) || val < 1 || val > 100) {
      toast.error("Enter a whole number from 1 to 100");
      return;
    }
    setSavingBattery(true);
    try {
      await completeCharging(batteryRec.id, val);
      toast.success(`${batteryRec.vehicle} battery set to ${val}% — vehicle available`);
      setBatteryRec(null);
      setBatteryVal("");
      await load();
      onChanged?.();
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.info("This session was already completed by another admin");
        setBatteryRec(null);
        setBatteryVal("");
        await load();
        onChanged?.();
      } else {
        toast.error(err?.response?.data?.detail || "Could not complete charging");
      }
    } finally {
      setSavingBattery(false);
    }
  };

  const inputCls =
    "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-[#0F172A] outline-none focus:border-[#5C2D91] focus:ring-1 focus:ring-[#5C2D91]";

  return (
    <div className="space-y-6" data-testid="maintenance-tab">
      {/* Form */}
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold text-[#0F172A]">
          <Wrench className="h-5 w-5 text-[#5C2D91]" />
          {editId ? "Edit Maintenance / Charging" : "Set Maintenance / Charging"}
        </h2>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <div>
              <label className="mb-1 block text-xs font-semibold text-[#475569]">Vehicle</label>
              <select value={form.vehicle} onChange={(e) => set("vehicle", e.target.value)} className={inputCls} data-testid="maint-vehicle">
                {vehicles.map((v) => (
                  <option key={v.id} value={v.name}>{v.name}</option>
                ))}
              </select>
            </div>
            <DateTimePicker
              label="Start date/time"
              dateVal={form.start_date}
              timeVal={form.start_time}
              onDate={(v) => set("start_date", v)}
              onTime={(v) => set("start_time", v)}
              testid="maint-start"
            />
            <DateTimePicker
              label="End date/time"
              dateVal={form.end_date}
              timeVal={form.end_time}
              onDate={(v) => set("end_date", v)}
              onTime={(v) => set("end_time", v)}
              testid="maint-end"
            />
            <div>
              <label className="mb-1 block text-xs font-semibold text-[#475569]">Type</label>
              <select value={form.block_type} onChange={(e) => set("block_type", e.target.value)} className={inputCls} data-testid="maint-type">
                {TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center justify-center gap-1.5 rounded-lg bg-[#5C2D91] px-6 py-2 text-sm font-semibold text-white transition-all hover:bg-[#4A2475] disabled:opacity-60"
              data-testid="maint-submit"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {editId ? "Update" : "Set"}
            </button>
            {editId && (
              <button type="button" onClick={resetForm} className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-[#64748B] hover:bg-slate-50" data-testid="maint-cancel-edit">
                <X className="h-4 w-4" /> Cancel
              </button>
            )}
          </div>
        </form>
      </section>

      {/* Records */}
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="maintenance-records">
        <h2 className="mb-4 font-heading text-lg font-semibold text-[#0F172A]">Saved Records</h2>
        {loading ? (
          <div className="flex items-center gap-2 py-8 text-sm text-[#64748B]">
            <Loader2 className="h-4 w-4 animate-spin text-[#5C2D91]" /> Loading records…
          </div>
        ) : records.length === 0 ? (
          <p className="py-8 text-center text-sm text-[#64748B]" data-testid="maint-empty">No maintenance or charging records yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="mobile-cards w-full min-w-[860px] text-left text-sm" data-testid="maint-table">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-[#64748B]">
                  <th className="py-3 pr-4">Vehicle</th>
                  <th className="py-3 pr-4">Type</th>
                  <th className="py-3 pr-4">Start</th>
                  <th className="py-3 pr-4">End</th>
                  <th className="py-3 pr-4">Status / Battery</th>
                  <th className="py-3 pr-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 last:border-0" data-testid={`maint-row-${r.id}`}>
                    <td data-label="Vehicle" className="py-3 pr-4 font-medium text-[#0F172A]">{r.vehicle}</td>
                    <td data-label="Type" className="py-3 pr-4">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-300">
                        <TypeIcon type={r.block_type} className="h-3.5 w-3.5" /> {r.block_type}
                      </span>
                    </td>
                    <td data-label="Start" className="py-3 pr-4 text-[#0F172A]" data-testid={`maint-start-${r.id}`}>{fmtDT(r.start_at)}</td>
                    <td data-label="End" className="py-3 pr-4 text-[#0F172A]" data-testid={`maint-end-${r.id}`}>{fmtDT(r.end_at)}</td>
                    <td data-label="Status" className="py-3 pr-4"><StatusBadge r={r} /></td>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        {r.pending_battery && (
                          <button onClick={() => { setBatteryRec(r); setBatteryVal(""); }} className="flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white transition-all hover:bg-amber-600" data-testid={`maint-enter-battery-${r.id}`}>
                            <BatteryCharging className="h-3.5 w-3.5" /> Enter battery
                          </button>
                        )}
                        <button onClick={() => startEdit(r)} className="flex items-center gap-1.5 rounded-lg bg-[#5C2D91] px-3 py-1.5 text-xs font-semibold text-white transition-all hover:bg-[#4A2475]" data-testid={`maint-edit-${r.id}`}>
                          <Pencil className="h-3.5 w-3.5" /> Edit
                        </button>
                        <button onClick={() => setDeleteRec(r)} className="flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 transition-all hover:bg-red-100" data-testid={`maint-delete-${r.id}`}>
                          <Trash2 className="h-3.5 w-3.5" /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Inline battery entry */}
      {batteryRec && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="maint-battery-modal">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-3 flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#5C2D91]/10 text-[#5C2D91]">
                <BatteryCharging className="h-4.5 w-4.5" />
              </span>
              <h3 className="text-base font-bold text-[#0F172A]">Enter battery %</h3>
            </div>
            <p className="text-sm text-[#64748B]">
              Charging for <span className="font-semibold text-[#0F172A]">{batteryRec.vehicle}</span> ended {fmtDT(batteryRec.end_at)}. Record the battery % to mark it Available.
            </p>
            <label className="mt-4 block text-xs font-semibold text-[#475569]">Current battery % (1–100)</label>
            <input
              type="number"
              min={1}
              max={100}
              step={1}
              inputMode="numeric"
              value={batteryVal}
              onChange={(e) => setBatteryVal(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitBattery(); }}
              autoFocus
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-[#0F172A] outline-none focus:border-[#5C2D91] focus:ring-1 focus:ring-[#5C2D91]"
              data-testid="maint-battery-input"
            />
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => { setBatteryRec(null); setBatteryVal(""); }} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-[#64748B] hover:bg-slate-50" data-testid="maint-battery-cancel">
                Cancel
              </button>
              <button onClick={submitBattery} disabled={savingBattery} className="flex items-center gap-1.5 rounded-lg bg-[#5C2D91] px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-[#4A2475] disabled:opacity-60" data-testid="maint-battery-confirm">
                {savingBattery ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />} Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteRec && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="maint-delete-modal">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-base font-bold text-[#0F172A]">Delete record?</h3>
            <p className="mt-2 text-sm text-[#64748B]">
              This will remove the {deleteRec.block_type.toLowerCase()} period for <span className="font-semibold text-[#0F172A]">{deleteRec.vehicle}</span> ({fmtDT(deleteRec.start_at)} – {fmtDT(deleteRec.end_at)}) and release the slot.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setDeleteRec(null)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-[#64748B] hover:bg-slate-50" data-testid="maint-delete-cancel">
                Cancel
              </button>
              <button onClick={confirmDelete} disabled={deleting} className="flex items-center gap-1.5 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-red-700 disabled:opacity-60" data-testid="maint-delete-confirm">
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
